import { useEffect, useRef, useState } from "react"
import type { Evento } from "../api/tipi"

/** Cloudflare chiude le connessioni inattive a 100 secondi: un ping ogni 30
 *  tiene su chi guarda da remoto durante le pause pubblicitarie. */
const PING_MS = 30_000
const RIPRESA_MIN_MS = 1_000
const RIPRESA_MAX_MS = 20_000

export type StatoCanale = "collegato" | "in-ripresa" | "spento"

/** Stanza WebSocket dell'edizione. Il canale è un lusso, non una necessità:
 *  se cade, il voto continua a passare da HTTP e la UI degrada in polling. */
export function useCanale(
  editionId: string | null,
  token: string | null,
  suEvento: (e: Evento) => void,
  suRevoca?: (codice: 4401 | 4403) => void,
): StatoCanale {
  const [stato, setStato] = useState<StatoCanale>("spento")
  const riferimentoCallback = useRef(suEvento)
  riferimentoCallback.current = suEvento
  const riferimentoRevoca = useRef(suRevoca)
  riferimentoRevoca.current = suRevoca

  useEffect(() => {
    if (!editionId || !token) return

    let vivo = true
    let ws: WebSocket | null = null
    let ping: ReturnType<typeof setInterval> | undefined
    let ripresa: ReturnType<typeof setTimeout> | undefined
    let attesa = RIPRESA_MIN_MS

    const collega = () => {
      if (!vivo) return
      const schema = location.protocol === "https:" ? "wss" : "ws"
      ws = new WebSocket(
        `${schema}://${location.host}/ws/editions/${editionId}?token=${encodeURIComponent(token)}`,
      )

      ws.onopen = () => {
        if (!vivo) return
        attesa = RIPRESA_MIN_MS
        setStato("collegato")
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), PING_MS)
      }

      ws.onmessage = (messaggio) => {
        try {
          riferimentoCallback.current(JSON.parse(messaggio.data) as Evento)
        } catch {
          /* un frame illeggibile non deve far cadere la stanza */
        }
      }

      ws.onclose = (chiusura) => {
        clearInterval(ping)
        if (!vivo) return
        // 4401: la sessione non vale più (reset della password). 4403: fuori
        // dal gruppo. Riprovare sarebbe inutile: si esce e si dice perché.
        const codice = chiusura.code
        if (codice === 4401 || codice === 4403) {
          vivo = false
          setStato("spento")
          riferimentoRevoca.current?.(codice)
          return
        }
        setStato("in-ripresa")
        ripresa = setTimeout(collega, attesa)
        attesa = Math.min(attesa * 2, RIPRESA_MAX_MS)
      }

      ws.onerror = () => ws?.close()
    }

    collega()

    return () => {
      vivo = false
      clearInterval(ping)
      clearTimeout(ripresa)
      ws?.close()
      setStato("spento")
    }
  }, [editionId, token])

  return stato
}
